// @ts-nocheck
import { useState, useEffect } from 'react'
import { useCallHistory } from '../hooks/useCallHistory'
import { useCallFormatters } from '../hooks/useCallFormatters'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { X, Frown, Meh, Smile, SmilePlus, Video, Trash2, Edit2, Clock, User } from 'lucide-react'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'

// デフォルト値（電話番号設定がない場合に使用）
const defaultCallTypeOptions = [
  { value: 'inquiry', label: '問い合わせ' },
  { value: 'complaint', label: 'クレーム' },
  { value: 'order', label: '注文' },
  { value: 'support', label: 'サポート' },
  { value: 'other', label: 'その他' },
]

const defaultQuickTags = [
  { value: 'callback', label: '折り返し希望', isDefault: false },
  { value: 'urgent', label: '緊急', isDefault: false },
  { value: 'vip', label: 'VIP', isDefault: false },
  { value: 'first-contact', label: '初回問い合わせ', isDefault: false },
]

export default function CallMemoEditor({ call, onClose, onSave }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [noteSegments, setNoteSegments] = useState([])
  const [loadingSegments, setLoadingSegments] = useState(false)
  const [editingSegmentId, setEditingSegmentId] = useState(null)

  // メタデータ（callType, satisfaction）用
  const [formData, setFormData] = useState({
    callType: '',
    satisfaction: ''
  })

  // 新規メモ入力用
  const [newNote, setNewNote] = useState({
    note: '',
    summary: '',
    tags: []
  })
  const [newTag, setNewTag] = useState('')

  // 電話番号から取得したオプション
  const [callTypeOptions, setCallTypeOptions] = useState(defaultCallTypeOptions)
  const [quickTags, setQuickTags] = useState(defaultQuickTags)

  const { fetchNoteSegments, createNoteSegment, updateNoteSegment, deleteNoteSegment, updateCallHistory } = useCallHistory()
  const { formatDuration, formatDateTime } = useCallFormatters()
  const currentUser = useAuthStore((state) => state.user)
  const api = useApi()

  // 電話番号の設定を取得
  useEffect(() => {
    const fetchPhoneNumberOptions = async () => {
      if (!call) return

      try {
        // 利用した自社の電話番号（着信時はto、発信時はfrom）
        const usedNumber = call.originalDirection === 'inbound' ? call.to : call.from
        if (!usedNumber) {
          // 電話番号がない場合はデフォルトを使用
          setCallTypeOptions(defaultCallTypeOptions)
          setQuickTags(defaultQuickTags)
          return
        }

        // 組織の電話番号リストを取得
        const phoneData = await api.get('/api/phone-numbers')
        // 通話で使用した電話番号に一致するものを検索
        const matchedPhone = phoneData.items?.find(p => p.phoneNumber === usedNumber)

        if (matchedPhone) {
          // 通話種別オプションの設定
          if (matchedPhone.callTypeOptions && matchedPhone.callTypeOptions.length > 0) {
            setCallTypeOptions(matchedPhone.callTypeOptions)
          } else {
            setCallTypeOptions(defaultCallTypeOptions)
          }

          // クイックタグの設定
          if (matchedPhone.quickTags && matchedPhone.quickTags.length > 0) {
            setQuickTags(matchedPhone.quickTags)
            
            // デフォルトタグを自動的に選択
            const defaultTags = matchedPhone.quickTags.filter(tag => tag.isDefault).map(tag => tag.label)
            if (defaultTags.length > 0) {
              setNewNote(prev => ({ ...prev, tags: defaultTags }))
            }
          } else {
            setQuickTags(defaultQuickTags)
          }
        } else {
          // 電話番号が見つからない場合はデフォルトを使用
          setCallTypeOptions(defaultCallTypeOptions)
          setQuickTags(defaultQuickTags)
        }
      } catch (err) {
        console.error('電話番号設定取得エラー:', err)
        // エラー時はデフォルト値を維持
        setCallTypeOptions(defaultCallTypeOptions)
        setQuickTags(defaultQuickTags)
      }
    }

    fetchPhoneNumberOptions()
  }, [call?.direction, call?.to, call?.from, api])

  // 初期化
  useEffect(() => {
    if (call) {
      setFormData({
        callType: call.callType || '',
        satisfaction: call.satisfaction || ''
      })
      loadNoteSegments()
    }
  }, [call])

  // NoteSegments読み込み
  const loadNoteSegments = async () => {
    if (!call?.callHistoryId) return

    try {
      setLoadingSegments(true)
      const segments = await fetchNoteSegments(call.callHistoryId)
      setNoteSegments(segments)
    } catch (err) {
      console.error('Failed to load note segments:', err)
    } finally {
      setLoadingSegments(false)
    }
  }

  // メタデータ変更
  const handleMetaChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  // 新規メモ変更
  const handleNewNoteChange = (e) => {
    const { name, value } = e.target
    setNewNote(prev => ({ ...prev, [name]: value }))
  }

  // タグ追加
  const handleAddTag = () => {
    if (newTag && !newNote.tags.includes(newTag)) {
      setNewNote(prev => ({
        ...prev,
        tags: [...prev.tags, newTag]
      }))
      setNewTag('')
    }
  }

  // タグ削除
  const handleRemoveTag = (tagToRemove) => {
    setNewNote(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  // メモセグメント追加
  const handleAddNoteSegment = async () => {
    if (!newNote.note.trim() && !newNote.summary.trim()) return

    try {
      setLoading(true)
      setError(null)

      await createNoteSegment(call.callHistoryId, {
        note: newNote.note,
        summary: newNote.summary,
        tags: newNote.tags,
        callType: formData.callType
      })

      // 入力をクリア
      setNewNote({ note: '', summary: '', tags: [] })

      // リストを更新
      await loadNoteSegments()
    } catch (err) {
      console.error('Failed to add note segment:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // メモセグメント削除
  const handleDeleteSegment = async (segmentId) => {
    if (!confirm('このメモを削除しますか？')) return

    try {
      setLoading(true)
      await deleteNoteSegment(call.callHistoryId, segmentId)
      await loadNoteSegments()
    } catch (err) {
      console.error('Failed to delete note segment:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // メタデータ保存
  const handleSaveMetadata = async () => {
    try {
      setLoading(true)
      setError(null)

      await updateCallHistory(call.callHistoryId, {
        callType: formData.callType,
        satisfaction: formData.satisfaction
      })

      onSave()
    } catch (err) {
      console.error('Failed to save metadata:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // 自分のメモかどうか判定
  const isOwnSegment = (segment) => {
    return segment.createdBy === currentUser?.userId
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">通話詳細・メモ編集</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* コンテンツ */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* 通話情報 */}
          <div className="mb-6 p-4 bg-gray-50 rounded-md">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">通話情報</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-600">日時: </span>
                <span className="text-gray-900">{formatDateTime(call.startTime, { format: 'full' })}</span>
              </div>
              <div>
                <span className="text-gray-600">通話時間: </span>
                <span className="text-gray-900">{formatDuration(call.duration)}</span>
              </div>
              <div>
                <span className="text-gray-600">発信者: </span>
                <span className="text-gray-900">{call.customerName || formatPhoneNumberForDisplay(call.from)}</span>
              </div>
              <div>
                <span className="text-gray-600">担当: </span>
                <span className="text-gray-900">{call.handledByName || '未対応'}</span>
              </div>
              {call.phoneNumberName && (
                <div className="col-span-2">
                  <span className="text-gray-600">着信先: </span>
                  <span className="text-gray-900">{call.phoneNumberName}</span>
                </div>
              )}
            </div>
          </div>

          {/* メタデータ編集（通話種別・満足度） */}
          <div className="mb-6 p-4 border border-gray-200 rounded-md">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">通話分類</h3>
            <div className="grid grid-cols-2 gap-4">
              {/* 通話種別 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  通話種別
                </label>
                <select
                  name="callType"
                  value={formData.callType}
                  onChange={handleMetaChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">未分類</option>
                  {callTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* 満足度 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  顧客満足度
                </label>
                <div className="flex gap-1">
                  {[
                    { value: 'poor', label: '不満', icon: Frown, color: 'bg-red-100 text-red-800 hover:bg-red-200' },
                    { value: 'neutral', label: '普通', icon: Meh, color: 'bg-gray-100 text-gray-800 hover:bg-gray-200' },
                    { value: 'good', label: '良好', icon: Smile, color: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
                    { value: 'excellent', label: '優秀', icon: SmilePlus, color: 'bg-green-100 text-green-800 hover:bg-green-200' }
                  ].map((option) => {
                    const Icon = option.icon
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, satisfaction: option.value }))}
                        className={`flex-1 px-2 py-2 rounded-md text-xs font-medium border-2 flex items-center justify-center gap-1 ${
                          formData.satisfaction === option.value
                            ? 'border-blue-500 ' + option.color
                            : 'border-gray-200 ' + option.color
                        }`}
                        title={option.label}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 既存メモ（後方互換性：読み取り専用） */}
          {call.notes && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">過去のメモ（読み取り専用）</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{call.notes}</p>
            </div>
          )}

          {/* メモセグメント一覧 */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">メモ履歴</h3>

            {loadingSegments ? (
              <div className="text-center py-4 text-gray-500">読み込み中...</div>
            ) : noteSegments.length === 0 ? (
              <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
                メモがありません
              </div>
            ) : (
              <div className="space-y-3">
                {noteSegments.map((segment) => (
                  <div
                    key={segment.noteSegmentId}
                    className={`p-4 border rounded-md ${
                      isOwnSegment(segment) ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    {/* セグメントヘッダー */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {segment.createdByName || '不明'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(segment.clientCreatedAt || segment.serverCreatedAt)}
                        </span>
                      </div>

                      {isOwnSegment(segment) && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDeleteSegment(segment.noteSegmentId)}
                            className="text-red-500 hover:text-red-700"
                            title="削除"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* 要約 */}
                    {segment.summary && (
                      <p className="text-sm font-medium text-gray-900 mb-1">{segment.summary}</p>
                    )}

                    {/* メモ本文 */}
                    {segment.note && (
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{segment.note}</p>
                    )}

                    {/* タグ */}
                    {segment.tags && segment.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {segment.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 新規メモ入力 */}
          <div className="p-4 border border-gray-200 rounded-md bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">新しいメモを追加</h3>

            {/* 要約 */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                要約
              </label>
              <input
                type="text"
                name="summary"
                value={newNote.summary}
                onChange={handleNewNoteChange}
                placeholder="通話の要約を簡潔に入力"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* 詳細メモ */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                詳細メモ
              </label>
              <textarea
                name="note"
                value={newNote.note}
                onChange={handleNewNoteChange}
                rows={4}
                placeholder="通話の詳細、対応内容、フォローアップ事項など"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* タグ */}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                タグ
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="新しいタグ"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  追加
                </button>
              </div>

              {/* クイックタグ */}
              {quickTags.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs text-gray-600 mb-1">よく使うタグ:</div>
                  <div className="flex flex-wrap gap-2">
                    {quickTags.map((tagOption) => {
                      // オブジェクトまたは文字列に対応
                      const label = typeof tagOption === 'string' ? tagOption : tagOption.label
                      const key = typeof tagOption === 'string' ? tagOption : tagOption.value
                      
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            if (!newNote.tags.includes(label)) {
                              setNewNote(prev => ({ ...prev, tags: [...prev.tags, label] }))
                            }
                          }}
                          disabled={newNote.tags.includes(label)}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          + {label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {newNote.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={handleAddNoteSegment}
              disabled={loading || (!newNote.note.trim() && !newNote.summary.trim())}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'メモを追加中...' : 'メモを追加'}
            </button>
          </div>

          {/* 録音リンク */}
          {call.recordingUrl && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 mb-2">
                <Video className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-gray-700 font-medium">録音データ</p>
              </div>
              <a
                href={call.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                録音を再生
              </a>
            </div>
          )}
        </div>

        {/* フッター */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            閉じる
          </button>
          <button
            onClick={handleSaveMetadata}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? '保存中...' : '分類を保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
